import React, { useMemo } from 'react';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import {
  dataGridTextSorter,
  dataGridDateSorter,
} from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useYesNoDataGridFilter } from '../../../../common/DataGrid/filters/DataGridFilters.jsx';
import PropTypes from 'prop-types';
import Spinner from '../../../../common/spinner/Spinner.jsx';
import { useTranslation } from 'react-i18next';
import { useDataGridDateRangeFilter } from '../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';

const EventsTable = ({ calendar, onDelete, onRowClick, onEditClick }) => {
  const { t } = useTranslation();
  const regex = useMemo(() => /<\/?[^>]+(>|$)/g, []);

  // Decode HTML entities safely using the browser parser
  const decodeHtml = useMemo(
    () => (s) => {
      if (!s) return '';
      const parser = new DOMParser();
      const doc = parser.parseFromString(String(s), 'text/html');
      return doc.documentElement.textContent || '';
    },
    [],
  );

  // Robim preto aby som naplnil 'to': datum hodnotou aby nebola null koli zlozitym podmienkam vo filtrovani
  const decoratedCalendarItems = useMemo(() => {
    return (
      calendar &&
      calendar.map((item) => {
        return {
          ...item,
          date_to: (item.to && item.to) || item.from,
          // Odstránenie HTML tagov a dekódovanie kódových znakov na čitateľný text
          title_sk: decodeHtml(
            String(item.title_sk || '').replace(regex, ''),
          ).trim(),
        };
      })
    );
  }, [calendar, decodeHtml, regex]);

  // DataGrid config.
  const titleSkTextFilterProps = useDataGridTextFilter('title_sk');
  const fromDateRangeFilterProps = useDataGridDateRangeFilter('from');
  const toDateRangeFilterProps = useDataGridDateRangeFilter('to');
  const hiddenBooleanFilterProps = useYesNoDataGridFilter('hidden');
  const bcBooleanFilterProps = useYesNoDataGridFilter('B');
  const ingBooleanFilterProps = useYesNoDataGridFilter('E');
  const phdBooleanFilterProps = useYesNoDataGridFilter('D');
  const podujatieBooleanFilterProps = useYesNoDataGridFilter('P');

  const dataGridColumns = useMemo(
    () => [
      {
        title: t('Názov (slovensky)'),
        dataIndex: 'title_sk',
        ellipsis: true,
        sorter: dataGridTextSorter('title_sk'),
        ...titleSkTextFilterProps,
        width: 500,
      },
      {
        title: t('Začiatok'),
        dataIndex: 'from',
        ellipsis: true,
        sorter: dataGridDateSorter('from'),
        render: (date) => (date && new Date(date).toLocaleDateString()) || '-',
        ...fromDateRangeFilterProps,
        width: 95,
      },
      {
        title: t('Koniec'),
        dataIndex: 'to',
        ellipsis: true,
        sorter: dataGridDateSorter('to'),
        render: (date) => (date && new Date(date).toLocaleDateString()) || '-',
        ...toDateRangeFilterProps,
        width: 95,
      },
      {
        title: t('Skrytá'),
        dataIndex: 'hidden',
        ...hiddenBooleanFilterProps,
        width: 80,
      },
      {
        title: t('Bc.'),
        dataIndex: 'B',
        ...bcBooleanFilterProps,
        width: 80,
      },
      {
        title: t('Ing.'),
        dataIndex: 'E',
        ...ingBooleanFilterProps,
        width: 80,
      },
      {
        title: t('PhD'),
        dataIndex: 'D',
        ...phdBooleanFilterProps,
        width: 80,
      },
      {
        title: t('Podujatie'),
        dataIndex: 'P',
        ...podujatieBooleanFilterProps,
        width: 100,
      },
    ],
    [
      t,
      fromDateRangeFilterProps,
      toDateRangeFilterProps,
      titleSkTextFilterProps,
      hiddenBooleanFilterProps,
      bcBooleanFilterProps,
      ingBooleanFilterProps,
      phdBooleanFilterProps,
      podujatieBooleanFilterProps,
    ],
  );

  if (!calendar) {
    return <Spinner />;
  }

  return (
    <>
      <DataGrid
        rowKey={(event) => event.id}
        columns={dataGridColumns}
        dataSource={decoratedCalendarItems || []}
        permissionsBasic={'calendar'}
        uniqueTableId={'basic-calendar-table'}
        onRowClick={onEditClick}
        onEditClick={onEditClick}
        onDelete={onDelete}
      />
    </>
  );
};

EventsTable.propTypes = {
  calendar: PropTypes.array,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onCreateClick: PropTypes.func,
};

export default EventsTable;
